import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AssumptionsInput } from "@retirement-planner/contract";
import { useLanguage } from "../i18n/LanguageProvider";
import { useAssumptions, useSaveAssumptions } from "../hooks/api";
import { MoneyField, PercentField } from "../components/fields";
import { Button, StatePanel, Toast } from "../components/primitives";

export function AssumptionsScreen() {
  const { t } = useLanguage();
  const query = useAssumptions();
  const save = useSaveAssumptions();
  const navigate = useNavigate();
  const [form, setForm] = useState<AssumptionsInput | null>(null);

  useEffect(() => {
    if (query.data && !form) {
      const a = query.data;
      // First-run default for the required targetAnnualSpending: monthlyContribution x 12.
      const seededSpending =
        a.targetAnnualSpending.amountMinorUnits > 0
          ? a.targetAnnualSpending
          : { amountMinorUnits: a.monthlyContribution.amountMinorUnits * 12, currency: a.currency };
      setForm({ ...a, targetAnnualSpending: seededSpending });
    }
  }, [query.data, form]);

  if (query.isLoading || !form) return <StatePanel kind="loading" />;
  if (query.isError) return <StatePanel kind="error" onRetry={() => query.refetch()} />;

  const set = (patch: Partial<AssumptionsInput>) => setForm({ ...form, ...patch });
  const ageInvalid = form.targetRetirementAge <= form.currentAge;

  return (
    <main id="main-content" className="app-main" style={{ maxWidth: 560 }}>
      <h1>{t("assumptions")}</h1>

      <div className="field">
        <label htmlFor="cur-age">{t("currentAge")}</label>
        <div className="field__control">
          <input id="cur-age" type="number" className="tabular" value={form.currentAge}
            onChange={(e) => set({ currentAge: Number(e.target.value) })} />
        </div>
      </div>
      <div className="field" aria-invalid={ageInvalid ? "true" : undefined}>
        <label htmlFor="tgt-age">{t("targetRetirementAge")}</label>
        <div className="field__control">
          <input id="tgt-age" type="number" className="tabular" value={form.targetRetirementAge}
            onChange={(e) => set({ targetRetirementAge: Number(e.target.value) })} />
        </div>
        {ageInvalid && <span className="field__error">Target age must be greater than current age</span>}
      </div>

      <MoneyField label={t("currentSavings")} amountMinorUnits={form.currentSavings.amountMinorUnits}
        currency={form.currency} onChange={(v) => set({ currentSavings: { amountMinorUnits: v, currency: form.currency } })} />
      <MoneyField label={t("monthlyContribution")} amountMinorUnits={form.monthlyContribution.amountMinorUnits}
        currency={form.currency} onChange={(v) => set({ monthlyContribution: { amountMinorUnits: v, currency: form.currency } })} />
      <MoneyField label={t("targetAnnualSpending")} amountMinorUnits={form.targetAnnualSpending.amountMinorUnits}
        currency={form.currency} onChange={(v) => set({ targetAnnualSpending: { amountMinorUnits: v, currency: form.currency } })} />

      <PercentField label={t("expectedReturn")} value={form.expectedNominalReturnPct}
        onChange={(v) => set({ expectedNominalReturnPct: v })} />
      <PercentField label={t("inflation")} value={form.inflationRatePct} onChange={(v) => set({ inflationRatePct: v })} />
      <PercentField label={t("swr")} value={form.safeWithdrawalRatePct} onChange={(v) => set({ safeWithdrawalRatePct: v })} />

      <div className="field">
        <label htmlFor="plan-age">{t("planToAge")}</label>
        <div className="field__control">
          <input id="plan-age" type="number" className="tabular" min={1} max={120} value={form.planToAge}
            onChange={(e) => set({ planToAge: Number(e.target.value) })} />
        </div>
      </div>

      <Button variant="primary" loading={save.isPending} disabled={ageInvalid}
        onClick={() => save.mutate(form, { onSuccess: () => navigate("/") })}>
        {t("save")}
      </Button>
      {save.isError && <Toast message={t("somethingWrong")} assertive />}
    </main>
  );
}
